import type { ShortcutSuggestionData } from '@components/QuickPrompts/const';
import type { PaymentStatus } from '@services/pay';
import type { CreditModalSource, PaymentTrackingHandlers } from '@wegic/price-ui';
import type { ElementTaskAction, ElementTaskActionOrigin } from '@wegic/site-sdk/shared';
import type { ProjectLaunchOrderData } from '@/components/Markdown/MarkdownViewer/CustomTagCom/ProjectLaunchOrder/const';
import type {
  DatabaseInstanceStatusData,
  ProjectSourceChangedPayload,
  ProjectVersionCreatedData,
} from '@/services/website/socket/types';
import type { SelectedElement } from '@/types/element';

export const EVENTS = {
  /** 支付成功事件 */
  PAYMENT_SUCCESS: 'PAYMENT_SUCCESS',
  /** 积分更新事件 */
  PAYMENT_CREDIT_UPDATE: 'PAYMENT_CREDIT_UPDATE',
  /** 试用套餐支付提醒事件 */
  TRIAL_PACKAGE_PAYMENT_REMINDER: 'TRIAL_PACKAGE_PAYMENT_REMINDER',
  /** 网站生成完成事件 */
  WEBSITE_GENERATION_COMPLETED: 'WEBSITE_GENERATION_COMPLETED',
  /** 网站产物交付失败事件 */
  WEBSITE_GENERATION_FAILED: 'WEBSITE_GENERATION_FAILED',
  /** 用户确认首版网站的视觉方向，开始等待生成完成提醒 */
  INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED: 'INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED',
  /** 初版说明引导结束事件 */
  INITIAL_GUIDE_FINISHED: 'INITIAL_GUIDE_FINISHED',
  /** 媒体资源就绪事件（图片/视频轮询成功） */
  MEDIA_ASSETS_READY: 'MEDIA_ASSETS_READY',
  /** 构建状态变更事件 */
  BUILD_STATUS_CHANGE: 'BUILD_STATUS_CHANGE',
  /** 预览就绪事件 */
  PREVIEW_READY: 'PREVIEW_READY',
  /** 部署完成事件 */
  DEPLOY_FINISHED: 'DEPLOY_FINISHED',
  /** 用户切换事件 */
  USER_CHANGE: 'USER_CHANGE',
  /** 发送聊天消息事件 */
  SEND_CHAT_MESSAGE: 'SEND_CHAT_MESSAGE',
  /** 重试上一条消息事件 */
  RETRY_LAST_MESSAGE: 'RETRY_LAST_MESSAGE',
  /** 沙盒网页状态检测通知 */
  SANDBOX_WEBPAGE_STATUS_UPDATE: 'SANDBOX_WEBPAGE_STATUS_UPDATE',
  /** 插入文本到输入框事件 */
  INSERT_TEXT_TO_INPUT: 'INSERT_TEXT_TO_INPUT',
  /** 快捷指令事件 */
  SHORTCUT_SUGGESTION: 'SHORTCUT_SUGGESTION',
  /** 关闭下拉菜单事件 */
  DROPDOWN_MENU_CLOSE: 'DROPDOWN_MENU_CLOSE',
  /** 打开 299 试用包支付弹窗 */
  OPEN_TRIAL_PAY_MODAL: 'OPEN_TRIAL_PAY_MODAL',
  /** 299 支付 Checkout 拉起 loading 状态 */
  TRIAL_PAY_FLOW_LOADING: 'TRIAL_PAY_FLOW_LOADING',
  /** 项目版本创建 */
  PROJECT_VERSION_CREATED: 'PROJECT_VERSION_CREATED',
  /** 刷新版本历史 */
  VERSION_HISTORY_REFRESH: 'VERSION_HISTORY_REFRESH',
  /** 队列消息开始消费 */
  QUEUE_ITEM_STARTED: 'QUEUE_ITEM_STARTED',
  /** 队列消息消费完成 */
  QUEUE_ITEM_COMPLETED: 'QUEUE_ITEM_COMPLETED',
  /** 队列消息消费失败 */
  QUEUE_ITEM_FAILED: 'QUEUE_ITEM_FAILED',
  /** 消息列表滚动到底部 */
  MESSAGE_LIST_SCROLL_TO_BOTTOM: 'MESSAGE_LIST_SCROLL_TO_BOTTOM',
  /** 消息列表滚动指定距离 */
  MESSAGE_LIST_SCROLL_BY: 'MESSAGE_LIST_SCROLL_BY',
  /** 消息列表滚动状态变化 */
  MESSAGE_LIST_SCROLL: 'MESSAGE_LIST_SCROLL',
  /** 用户消息已发送 */
  USER_MESSAGE_SENT: 'USER_MESSAGE_SENT',
  /** Element Inspector 手动编辑直发 */
  ELEMENT_INSPECTOR_DIRECT_SEND: 'ELEMENT_INSPECTOR_DIRECT_SEND',
  /** Element Inspector 手动编辑直发送前资格预检 */
  ELEMENT_INSPECTOR_DIRECT_SEND_PRECHECK: 'ELEMENT_INSPECTOR_DIRECT_SEND_PRECHECK',
  /** Element Inspector 元素任务动作 */
  ELEMENT_INSPECTOR_TASK_ACTION: 'ELEMENT_INSPECTOR_TASK_ACTION',
  /** 展开任务列表 */
  EXPAND_TASK_LIST: 'EXPAND_TASK_LIST',
  /** 更新项目记忆任务列表 */
  UPDATE_PROJECT_MEMORY_TASKS: 'UPDATE_PROJECT_MEMORY_TASKS',
  /** 更新项目记忆需求总结字段 */
  UPDATE_PROJECT_MEMORY_SUMMARY: 'UPDATE_PROJECT_MEMORY_SUMMARY',
  /** 数据库实例状态变更 */
  DATABASE_INSTANCE_STATUS: 'DATABASE_INSTANCE_STATUS',
  /** 项目源码变更 */
  PROJECT_SOURCE_CHANGED: 'PROJECT_SOURCE_CHANGED',
  /** 打开积分购买弹窗 */
  OPEN_CREDIT_PURCHASE_MODAL: 'OPEN_CREDIT_PURCHASE_MODAL',
  /** 关闭积分购买弹窗 */
  CLOSE_CREDIT_PURCHASE_MODAL: 'CLOSE_CREDIT_PURCHASE_MODAL',
} as const;

export interface TrialPackagePaymentReminderData {
  projectId: string;
  sessionId: string;
  messageId?: string;
}

export interface WebsiteGenerationCompletedData {
  projectId: string;
  sessionId: string;
  messageId: string;
  timestamp: number;
  commitSha?: string;
  previewUrl?: string;
  initialFeedbackToolCall?: InitialGenerationFeedbackToolCallData;
}

export interface WebsiteGenerationFailedData {
  projectId: string;
  messageId: string;
  retryable: boolean;
  timestamp?: number;
}

export interface InitialWebsiteGenerationNotificationArmedData {
  projectId: string;
}

export interface InitialGenerationFeedbackToolCallData {
  toolId: string;
  toolName: 'initial_generation_feedback';
  status?: 'in_progress' | 'completed' | 'incomplete';
  arguments?: string;
  output?: string;
  messageId?: string;
  timestamp?: number;
}

export interface BuildStatusChangeData {
  projectId: string;
  status: 'pending' | 'building' | 'success' | 'failed';
  message?: string;
}

export interface PreviewReadyData {
  projectId: string;
  previewUrl: string;
}

export interface MediaAssetsReadyData {
  projectId?: string;
  messageId: string;
  readyAt: number;
  readyTypes: { image: boolean; video: boolean };
  urls: { image: string[]; video: string[] };
  /** 是否存在需要多次轮询才就绪的资源（任意 URL 轮询次数 > 1 即为 true） */
  hasRetryPolling?: boolean;
}

export interface DeployFinishedData {
  projectId: string;
  deployUrl: string;
  success: boolean;
  message?: string;
}

export interface SendChatMessageData {
  /** UI 中展示的消息文本 */
  text: string;
  /** 实际发送给 Agent 的输入文本，缺省时使用 text */
  agentRequestText?: string;
  /** 提交来源，用于区分用户主动发送和自动输入 */
  source?: 'manual' | 'auto-input';
  /** 消息展示样式，用于区分系统代用户拼合出的自动消息 */
  displayVariant?: 'composed';
  /** 是否自动发送，false 则填充到输入框 */
  autoSend?: boolean;
}

export interface SandboxWebpageStatusData {
  hasError: boolean;
  errorMessage: string;
}

export interface InsertTextToInputData {
  text: string;
}

export interface QueueItemStartedData {
  projectId: string;
  id: string;
  messageId: string;
}

export interface QueueItemCompletedData {
  projectId: string;
  id: string;
  messageId: string;
  nextPending: boolean;
}

export interface QueueItemFailedData {
  projectId: string;
  id: string;
  messageId: string | null;
  error: string;
  nextPending: boolean;
}

export interface MessageListScrollData {
  isUserScroll: boolean;
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
}

export interface ElementInspectorDirectSendData {
  requestId: string;
  projectId: string;
  sandboxId: string;
  element: SelectedElement;
}

export interface ElementInspectorDirectSendPrecheckData {
  requestId: string;
  projectId: string;
  sandboxId: string;
}

export interface ElementInspectorTaskActionData {
  requestId: string;
  projectId: string;
  sandboxId: string;
  elementId: string;
  queueItemId: string;
  action: ElementTaskAction;
  origin: ElementTaskActionOrigin;
}

export interface OpenCreditPurchaseModalData {
  source: CreditModalSource;
  defaultSelectedCredits?: number;
  trackingHandlers?: PaymentTrackingHandlers;
}

export interface ProjectMemorySummaryFieldsUpdateData {
  updatedFields: string[];
}

export interface TrialPayFlowContext {
  order?: ProjectLaunchOrderData | null;
  ctaSurface?: 'chat' | 'launch_panel';
  projectId?: string;
  loginAfterPayment?: boolean;
}

export type GlobalEvent = {
  [EVENTS.PAYMENT_SUCCESS]: [PaymentStatus];
  [EVENTS.PAYMENT_CREDIT_UPDATE]: [number];
  [EVENTS.TRIAL_PACKAGE_PAYMENT_REMINDER]: [TrialPackagePaymentReminderData];
  [EVENTS.WEBSITE_GENERATION_COMPLETED]: [WebsiteGenerationCompletedData];
  [EVENTS.WEBSITE_GENERATION_FAILED]: [WebsiteGenerationFailedData];
  [EVENTS.INITIAL_WEBSITE_GENERATION_NOTIFICATION_ARMED]: [InitialWebsiteGenerationNotificationArmedData];
  [EVENTS.INITIAL_GUIDE_FINISHED]: [];
  [EVENTS.MEDIA_ASSETS_READY]: [MediaAssetsReadyData];
  [EVENTS.BUILD_STATUS_CHANGE]: [BuildStatusChangeData];
  [EVENTS.PREVIEW_READY]: [PreviewReadyData];
  [EVENTS.DEPLOY_FINISHED]: [DeployFinishedData];
  [EVENTS.USER_CHANGE]: [];
  [EVENTS.SEND_CHAT_MESSAGE]: [SendChatMessageData];
  [EVENTS.USER_MESSAGE_SENT]: [];
  [EVENTS.RETRY_LAST_MESSAGE]: [];
  [EVENTS.SANDBOX_WEBPAGE_STATUS_UPDATE]: [SandboxWebpageStatusData];
  [EVENTS.INSERT_TEXT_TO_INPUT]: [InsertTextToInputData];
  [EVENTS.SHORTCUT_SUGGESTION]: [ShortcutSuggestionData];
  [EVENTS.DROPDOWN_MENU_CLOSE]: [];
  [EVENTS.OPEN_TRIAL_PAY_MODAL]: [TrialPayFlowContext?];
  [EVENTS.TRIAL_PAY_FLOW_LOADING]: [boolean];
  [EVENTS.PROJECT_VERSION_CREATED]: [ProjectVersionCreatedData];
  [EVENTS.VERSION_HISTORY_REFRESH]: [];
  [EVENTS.QUEUE_ITEM_STARTED]: [QueueItemStartedData];
  [EVENTS.QUEUE_ITEM_COMPLETED]: [QueueItemCompletedData];
  [EVENTS.QUEUE_ITEM_FAILED]: [QueueItemFailedData];
  [EVENTS.MESSAGE_LIST_SCROLL_TO_BOTTOM]: [];
  [EVENTS.MESSAGE_LIST_SCROLL_BY]: [{ deltaY: number }];
  [EVENTS.MESSAGE_LIST_SCROLL]: [MessageListScrollData];
  [EVENTS.ELEMENT_INSPECTOR_DIRECT_SEND]: [ElementInspectorDirectSendData];
  [EVENTS.ELEMENT_INSPECTOR_DIRECT_SEND_PRECHECK]: [ElementInspectorDirectSendPrecheckData];
  [EVENTS.ELEMENT_INSPECTOR_TASK_ACTION]: [ElementInspectorTaskActionData];
  [EVENTS.EXPAND_TASK_LIST]: [];
  [EVENTS.UPDATE_PROJECT_MEMORY_TASKS]: [];
  [EVENTS.UPDATE_PROJECT_MEMORY_SUMMARY]: [ProjectMemorySummaryFieldsUpdateData];
  [EVENTS.DATABASE_INSTANCE_STATUS]: [DatabaseInstanceStatusData['data']];
  [EVENTS.PROJECT_SOURCE_CHANGED]: [ProjectSourceChangedPayload];
  [EVENTS.OPEN_CREDIT_PURCHASE_MODAL]: [OpenCreditPurchaseModalData];
  [EVENTS.CLOSE_CREDIT_PURCHASE_MODAL]: [];
} & { [T in typeof EVENTS[keyof typeof EVENTS]]: unknown };
